import { computed, ComputedRef, Ref, WritableComputedRef } from "vue";
import type { DSBaseListOption, DSSListOptionGroup } from "@/src/design_system/form_elements/option_list/types";
import type {
  DSSelectNormalizedModelValue,
  DSSelectNormalizedOption,
  DSSelectProps,
} from "@/src/design_system/form_elements/select/types/props";
import { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";

interface UseSelectSelectionArgs {
  modelValue: Ref<DSSelectNormalizedModelValue>;
  props: ComputedRef<Readonly<DSSelectProps>>;
  onSingleSelect?: (value: DSBaseListOption | null) => void;
}

interface UseSelectSelection {
  baseOptions: ComputedRef<DSBaseListOption[]>;
  selectedValues: ComputedRef<DSOptionValue[]>;
  selectedOptions: ComputedRef<DSBaseListOption[]>;
  displayValue: ComputedRef<string>;
  removeOption: (value?: DSOptionValue) => void;
  showSelectedTags: ComputedRef<boolean>;
  optionListModelValue: WritableComputedRef<DSOptionValue | DSOptionValue[]>;
}

export const useSelectSelection = ({
  modelValue,
  props,
  onSingleSelect,
}: UseSelectSelectionArgs): UseSelectSelection => {
  const baseOptions = computed<DSBaseListOption[]>(() => {
    return (props.value.options as DSSListOptionGroup[]).flatMap(option => option.options || [option]);
  });

  const selectedOptions = computed<DSBaseListOption[]>(() => {
    if (!modelValue.value) return [];

    const normalized = Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value];
    return normalized.filter((option): option is DSSelectNormalizedOption => Boolean(option));
  });

  const selectedValues = computed<DSOptionValue[]>(() => {
    return selectedOptions.value.map(option => option.value ?? null);
  });

  const displayValue = computed<string>(() => {
    if (!selectedOptions.value.length) return "";
    if (props.value.multiple) {
      return selectedOptions.value.map(option => option.label ?? "").join(", ");
    }
    return selectedOptions.value[0]?.label ?? "";
  });

  const removeOption = (value?: DSOptionValue) => {
    if (value === undefined) return;

    if (Array.isArray(modelValue.value)) {
      modelValue.value = modelValue.value.filter(option => option?.value !== value);
      return;
    }

    if (modelValue.value?.value === value) {
      modelValue.value = null;
    }
  };

  const showSelectedTags = computed<boolean>(() => Boolean(props.value.multiple && selectedOptions.value.length > 0));

  const findOptionByValue = (value?: DSOptionValue): DSSelectNormalizedOption | undefined => {
    if (value === undefined) return undefined;
    return baseOptions.value.find((option): option is DSSelectNormalizedOption => option.value === value);
  };

  const getMultipleModelValue = (value?: DSOptionValue | DSOptionValue[]): DSSelectNormalizedOption[] => {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    const selectedOptionsByValue = new Map(selectedOptions.value.map(option => [option.value, option] as const));

    return values
      .map(selectedValue => findOptionByValue(selectedValue) ?? selectedOptionsByValue.get(selectedValue))
      .filter((option): option is DSSelectNormalizedOption => Boolean(option));
  };

  const getSingleModelValue = (value?: DSOptionValue | DSOptionValue[]): DSSelectNormalizedOption | null => {
    const normalizedValue = Array.isArray(value) ? value[0] : value;
    if (normalizedValue === undefined) return null;

    return findOptionByValue(normalizedValue) ?? null;
  };

  const updateModelValueFromOptionList = (value?: DSOptionValue | DSOptionValue[]) => {
    if (props.value.multiple) {
      modelValue.value = getMultipleModelValue(value);
      return;
    }

    const nextValue = getSingleModelValue(value);
    modelValue.value = nextValue;
    onSingleSelect?.(nextValue);
  };

  const optionListModelValue = computed<DSOptionValue | DSOptionValue[]>({
    get: () => {
      if (props.value.multiple) {
        return [...selectedValues.value];
      }
      return selectedValues.value[0] ?? "";
    },
    set: value => {
      updateModelValueFromOptionList(value);
    },
  });

  return {
    baseOptions,
    selectedValues,
    selectedOptions,
    displayValue,
    removeOption,
    showSelectedTags,
    optionListModelValue,
  };
};
